import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { getPatientPortalHealthAreaId } from "@/lib/patientExtensionHealthArea";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Package, Loader2, Star } from "lucide-react";
import { toast } from "sonner";
import * as LucideIcons from "lucide-react";
import { Button } from "@/components/ui/button";
import type { LucideIcon } from "lucide-react";

type Extension = {
  id: string;
  title: string;
  slug: string;
  full_description: string | null;
  icon_name: string;
  pricing_type: string;
  price_cents: number;
  is_active: boolean;
  is_suspended: boolean;
  source_path: string;
  health_areas: { name: string }[];
};

export default function AplicativosPaciente() {
  const [extensions, setExtensions] = useState<Extension[]>([]);
  const [loading, setLoading] = useState(true);
  const [missingPatientHealthArea, setMissingPatientHealthArea] = useState(false);
  const [favoriteIds, setFavoriteIds] = useState<Set<string>>(new Set());
  const navigate = useNavigate();

  useEffect(() => {
    void loadExtensions();
    void loadFavorites();
  }, []);

  const loadExtensions = async () => {
    try {
      setMissingPatientHealthArea(false);
      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (!user) return;

      const patientHealthAreaId = await getPatientPortalHealthAreaId(supabase);
      if (!patientHealthAreaId) {
        setMissingPatientHealthArea(true);
        setExtensions([]);
        return;
      }

      const { data: extensionAreas } = await supabase
        .from("extension_health_areas")
        .select(`
          extension_id,
          extensions (
            id,
            title,
            slug,
            full_description,
            icon_name,
            pricing_type,
            price_cents,
            is_active,
            is_suspended,
            source_path
          )
        `)
        .eq("health_area_id", patientHealthAreaId);

      if (!extensionAreas) {
        setExtensions([]);
        return;
      }

      const extensionsWithAreas = await Promise.all(
        extensionAreas.map(async (item) => {
          const ext = item.extensions as Omit<Extension, "health_areas">;

          const { data: areas } = await supabase
            .from("extension_health_areas")
            .select(`
              health_areas (name)
            `)
            .eq("extension_id", ext.id);

          return {
            ...ext,
            health_areas:
              (areas?.map((a) => a.health_areas).filter(Boolean) as { name: string }[]) || [],
          } as Extension;
        }),
      );

      setExtensions(extensionsWithAreas.filter((e) => e.is_active && !e.is_suspended));
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : "Erro desconhecido";
      toast.error("Erro ao carregar aplicativos: " + errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const loadFavorites = async () => {
    try {
      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (!user) return;

      const { data: favorites } = await supabase
        .from("user_favorite_extensions")
        .select("extension_id")
        .eq("user_id", user.id);

      if (favorites) {
        setFavoriteIds(new Set(favorites.map((f) => f.extension_id)));
      }
    } catch (error) {
      console.error("Erro ao carregar favoritos:", error);
    }
  };

  const toggleFavorite = async (extensionId: string, e: React.MouseEvent) => {
    e.stopPropagation();

    try {
      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (!user) {
        toast.error("Voce precisa estar autenticado para favoritar aplicativos");
        return;
      }

      const isFavorite = favoriteIds.has(extensionId);

      if (isFavorite) {
        const { error } = await supabase
          .from("user_favorite_extensions")
          .delete()
          .eq("user_id", user.id)
          .eq("extension_id", extensionId);

        if (error) {
          throw new Error(error.message || "Erro ao remover favorito");
        }

        setFavoriteIds((prev) => {
          const next = new Set(prev);
          next.delete(extensionId);
          return next;
        });
        toast.success("Aplicativo removido dos favoritos");
      } else {
        const { error } = await supabase
          .from("user_favorite_extensions")
          .insert({
            user_id: user.id,
            extension_id: extensionId,
          });

        if (error) {
          throw new Error(error.message || "Erro ao adicionar favorito");
        }

        setFavoriteIds((prev) => new Set(prev).add(extensionId));
        toast.success("Aplicativo adicionado aos favoritos");
      }
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : "Erro ao favoritar aplicativo";
      toast.error(errorMessage);
    }
  };

  const formatPrice = (cents: number) =>
    new Intl.NumberFormat("pt-BR", {
      style: "currency",
      currency: "BRL",
    }).format(cents / 100);

  const getIcon = (iconName: string) => {
    const icons = LucideIcons as unknown as Record<string, LucideIcon>;
    const Icon = icons[iconName] || Package;
    return Icon;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (extensions.length === 0) {
    return (
      <div className="space-y-6">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Aplicativos</h1>
          <p className="text-muted-foreground">
            {missingPatientHealthArea
              ? "A area de saude Paciente nao esta configurada no sistema. Entre em contato com o suporte."
              : "Nenhum aplicativo disponivel para pacientes no momento."}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold tracking-tight">Aplicativos</h1>
        <p className="text-muted-foreground">
          Aplicativos liberados para pacientes no cadastro do sistema.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {extensions.map((ext) => {
          const Icon = getIcon(ext.icon_name);

          return (
            <Card
              key={ext.id}
              className="cursor-pointer hover:shadow-lg transition-shadow"
              onClick={() => navigate(`/paciente/aplicativos/${ext.slug}`)}
            >
              <CardHeader>
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-3">
                    <div className="p-2 rounded-lg bg-primary/10">
                      <Icon className="h-6 w-6 text-primary" />
                    </div>
                    <div className="min-w-0">
                      <CardTitle className="text-lg">{ext.title}</CardTitle>
                      {ext.full_description?.trim() ? (
                        <p className="mt-1 text-sm font-normal text-muted-foreground line-clamp-4">
                          {ext.full_description.trim()}
                        </p>
                      ) : null}
                    </div>
                  </div>
                  <div className="flex flex-col items-center gap-1">
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-8 w-8 p-0"
                      onClick={(e) => toggleFavorite(ext.id, e)}
                    >
                      <Star
                        className={`h-4 w-4 ${
                          favoriteIds.has(ext.id)
                            ? "fill-yellow-400 text-yellow-400"
                            : "text-muted-foreground"
                        }`}
                      />
                    </Button>
                    <p className="text-center text-[10px] leading-tight text-muted-foreground">
                      {favoriteIds.has(ext.id) ? (
                        <>
                          Remover
                          <br />
                          dos favoritos
                        </>
                      ) : (
                        <>
                          Adicionar
                          <br />
                          aos favoritos
                        </>
                      )}
                    </p>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="flex flex-wrap gap-2">
                  {ext.pricing_type === "free" ? (
                    <Badge variant="secondary">Gratuito</Badge>
                  ) : (
                    <Badge variant="default">{formatPrice(ext.price_cents)}</Badge>
                  )}

                  {ext.health_areas.map((area, idx) => (
                    <Badge key={idx} variant="outline">
                      {area.name}
                    </Badge>
                  ))}
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>
    </div>
  );
}
